import React, { useRef, useCallback, useState, useMemo } from 'react';

const MiniMap = ({ nodes, connections, canvas, onPanChange, isDarkMode }) => {
  const miniMapRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { zoom, panX, panY } = canvas;

  // Calculate bounds of all nodes
  const bounds = useMemo(() => {
    if (nodes.length === 0) {
      return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      minY = Math.min(minY, node.y);
      maxX = Math.max(maxX, node.x + node.width);
      maxY = Math.max(maxY, node.y + node.height);
    });

    // Add padding
    const padding = 50;
    return {
      minX: minX - padding,
      minY: minY - padding,
      maxX: maxX + padding,
      maxY: maxY + padding
    };
  }, [nodes]);

  // Mini map dimensions
  const miniMapWidth = 180;
  const miniMapHeight = 120;

  // Calculate scale to fit all nodes in mini map
  const contentWidth = bounds.maxX - bounds.minX;
  const contentHeight = bounds.maxY - bounds.minY;
  const scale = Math.min(
    miniMapWidth / contentWidth,
    miniMapHeight / contentHeight,
    1
  );

  // Viewport dimensions in mini map space
  const viewportWidth = (window.innerWidth / zoom) * scale;
  const viewportHeight = (window.innerHeight / zoom) * scale;

  // Viewport position in mini map space
  const viewportX = ((-panX / zoom) - bounds.minX) * scale;
  const viewportY = ((-panY / zoom) - bounds.minY) * scale;

  // Handle click/drag on mini map to pan
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    handlePan(e);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    handlePan(e);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handlePan = useCallback((e) => {
    const rect = miniMapRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Calculate click position in mini map
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert to canvas coordinates
    const canvasX = (clickX / scale) + bounds.minX;
    const canvasY = (clickY / scale) + bounds.minY;

    // Center viewport on click position
    const newPanX = -(canvasX - (window.innerWidth / zoom / 2)) * zoom;
    const newPanY = -(canvasY - (window.innerHeight / zoom / 2)) * zoom;

    onPanChange({ panX: newPanX, panY: newPanY });
  }, [scale, bounds, zoom, onPanChange]);

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="absolute bottom-4 left-4 w-10 h-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title="Show mini map"
      >
        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      </button>
    );
  }

  const bgColor = isDarkMode ? '#374151' : '#f3f4f6';
  const borderColor = isDarkMode ? '#4b5563' : '#d1d5db';
  const nodeColor = isDarkMode ? '#6b7280' : '#9ca3af';
  const viewportColor = isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)';
  const viewportBorderColor = '#3b82f6';

  return (
    <div
      className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden"
      style={{ width: miniMapWidth + 8, height: miniMapHeight + 32 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Mini Map</span>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          title="Hide mini map"
        >
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Mini map canvas */}
      <svg
        ref={miniMapRef}
        width={miniMapWidth}
        height={miniMapHeight}
        className="cursor-pointer m-1"
        style={{ backgroundColor: bgColor }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Nodes */}
        <g transform={`translate(${-bounds.minX * scale}, ${-bounds.minY * scale}) scale(${scale})`}>
          {connections.map(conn => {
            const fromNode = nodes.find(n => n.id === conn.from);
            const toNode = nodes.find(n => n.id === conn.to);
            if (!fromNode || !toNode) return null;

            const fromX = fromNode.x + fromNode.width / 2;
            const fromY = fromNode.y + fromNode.height / 2;
            const toX = toNode.x + toNode.width / 2;
            const toY = toNode.y + toNode.height / 2;

            return (
              <line
                key={conn.id}
                x1={fromX}
                y1={fromY}
                x2={toX}
                y2={toY}
                stroke={nodeColor}
                strokeWidth={1 / scale}
              />
            );
          })}

          {nodes.map(node => (
            <rect
              key={node.id}
              x={node.x}
              y={node.y}
              width={node.width}
              height={node.height}
              fill={node.color || nodeColor}
              rx={node.type === 'rounded' ? node.height / 2 : 2}
              ry={node.type === 'rounded' ? node.height / 2 : 2}
            />
          ))}
        </g>

        {/* Viewport indicator */}
        <rect
          x={Math.max(0, Math.min(viewportX, miniMapWidth - viewportWidth))}
          y={Math.max(0, Math.min(viewportY, miniMapHeight - viewportHeight))}
          width={Math.min(viewportWidth, miniMapWidth)}
          height={Math.min(viewportHeight, miniMapHeight)}
          fill={viewportColor}
          stroke={viewportBorderColor}
          strokeWidth={2}
          rx={2}
          ry={2}
        />
      </svg>
    </div>
  );
};

export default MiniMap;
